import { useDispatch, useSelector, shallowEqual} from "react-redux"
import { setPendingCVs, setCvs } from "../redux/slices/cvSlice"
import { useQuery } from "@tanstack/react-query"
import { toast } from "react-toastify"
import { FormattedMessage } from "react-intl"

import { getCVsStatus, getCVsLabels } from "../services/cv"
import { useFetchCVs } from "./useFetchCVs"
import { useProcessedCVsWebSocket } from "../services/useProcessedCVsWebSocket"

const useFetchPendingCVsStatus = ({ cvIds, enabled }) => useQuery({
  queryKey: ["pendingCVsStatus", cvIds],
  queryFn: () => getCVsStatus(cvIds),
  refetchInterval: 5_000, // Poll every 5 seconds
  enabled
})

/**
 * Custom hook that polls for processed CVs.
 *
 * Initiates polling if this is the initial load or there are pending CVs.
 * It fetches the latest CVs and updates the pending CVs based on the fetched data.
 *
 * @returns {{
 *   cvs: any[],
 *   pendingCVs: any[],
 *   isFetching: boolean
 * }} An object containing the list of CVs, the list of pending CVs, and the loading status.
*/
export const useProcessedCVsPolling = ({ limit = 10000, offset = 0 }) => {
  const dispatch = useDispatch();

  const { cvs, pendingCVs, pagination } = useSelector((state) => state.cv, shallowEqual);
  const userId = useSelector((state) => state.auth.user.id);

  const { data: pendingStatusData } = useFetchPendingCVsStatus({
    cvIds: pendingCVs?.map((cv) => cv.id) ?? [],
    enabled: pendingCVs?.length > 0
  });

  // Push path: backend emits `cv-processed` when KGE finishes an analysis.
  // The listener invalidates the cvs query so the list refreshes immediately,
  // without waiting for the 5s status poll + refetchInterval cycle. Polling
  // stays in place as a fallback for dropped socket connections.
  useProcessedCVsWebSocket(pendingCVs?.length > 0);

  const isInitialLoad = cvs === null;
  const arePendingCVsCompleted = pendingStatusData?.statusList?.filter((pending) => pending.status?.toLowerCase() === "completed").length > 0;
  const enableRefetch = isInitialLoad || arePendingCVsCompleted;
  
  const pendingCVsFailed = pendingStatusData?.statusList?.filter((pending) => pending.status?.toLowerCase() === "failed");
  updateFailedCVs(dispatch, pendingCVs, pendingCVsFailed);

  const { isFetching } = useFetchCVs({
    userId,
    enableRefetch,
    callback: (fetchedCVs) => {
      if (!fetchedCVs.length) return;
      updateCVs(dispatch, fetchedCVs, pendingCVs)
    },
    refetchOnMount: isInitialLoad,
    limit,
    offset,
  });

  return {
    cvs: cvs ?? [],
    pendingCVs,
    pagination,
    isFetching,
  }
}

const updateFailedCVs = (dispatch, pendingCVs, pendingFailedCVs) => {
  if (!pendingCVs?.length || !pendingFailedCVs?.length) return;

  const confirmedFailed = pendingFailedCVs.filter((failed) => {
    const failedCV = pendingCVs.find((cv) => cv.id === failed.id);
    if (!failedCV) return false;

    toast.error(
      <FormattedMessage
        id="toast.errorAnalyzingCV"
        values={{ fileName: failedCV.information.file_name }}
      />
    );
    return true;
  });

  if (!confirmedFailed.length) return;

  const updatedPendingCVs = pendingCVs.filter((pendingCV) =>
    !confirmedFailed.some((failed) => failed.id === pendingCV.id)
  );

  dispatch(setPendingCVs(updatedPendingCVs));
};

const updateCVs = (dispatch, fetchedCVs, pendingCVs) => {
  updatePendingCVs(dispatch, fetchedCVs, pendingCVs);
  dispatch(setCvs(fetchedCVs));
}

const updatePendingCVs = (dispatch, fetchedCVs, pendingCVs) => {
  if (!pendingCVs?.length) return;

  // Use a Set for faster lookup of processed CV IDs
  const cvsIdSet = new Set(fetchedCVs.map((cv) => cv.id));

  // Determine which pending CVs are no longer pending
  const processedPending = pendingCVs.filter(
    (pendingCV) => !cvIsStillPending(pendingCV, cvsIdSet)
  );

  if (processedPending.length) {
    const updatedPendingCVs = pendingCVs.filter(
      (pendingCV) => !processedPending.includes(pendingCV)
    );

    toast.success(
      <FormattedMessage
        id="toast.cvsProcessed"
        values={{ processedAmount: pendingCVs.length - updatedPendingCVs.length }}
      />
    );

    dispatch(setPendingCVs(updatedPendingCVs));
  }
};

const cvIsStillPending = (pendingCV, cvsIdSet) =>
  !cvsIdSet.has(pendingCV.id) && pendingCV.status === "IN_PROGRESS";
